import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Pill-shaped chip used to open the TaskAccountMenu / TaskAssigneeMenu /
 * TaskStatusMenu / TaskStartAtMenu popovers. Single visual language for the
 * new-task composer, task board cards, and the task detail drawer — so
 * a task looks the same while being created, while on the board, and while
 * being inspected.
 *
 * Use {@link TaskChip} for an interactive trigger (renders a `<button>`) and
 * {@link TaskChipBadge} for a static label (renders a `<span>`, no hover/
 * focus). `filled` swaps text-[#777] → text-[#1a1a1a] to indicate a value
 * is set.
 */

const TASK_CHIP_BASE = cn(
  "inline-flex h-7 items-center gap-1.5 rounded-full border border-[#eee] bg-white pl-2 pr-3 text-[13px] font-[450] leading-[1.4] tracking-[-0.02em] shadow-[0px_1px_1px_rgba(0,0,0,0.06)] outline-none transition-[transform,background-color,color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]",
  "hover:bg-neutral-02 hover:text-neutral-11 focus-visible:bg-neutral-02 data-[state=open]:bg-neutral-02",
  "active:scale-[0.97] motion-reduce:active:scale-100",
  "disabled:opacity-60",
);

function chipColor(filled: boolean) {
  return filled ? "text-[#1a1a1a]" : "text-[#777]";
}

type TaskChipProps = Omit<
  React.ComponentPropsWithoutRef<"button">,
  "children"
> & {
  /** Set color: filled → text-[#1a1a1a]; otherwise → text-[#777]. */
  filled?: boolean;
  children?: React.ReactNode;
};

/**
 * Interactive chip. Forwards refs so Radix's `asChild` slot works when this
 * is used as a popover trigger.
 */
export const TaskChip = React.forwardRef<HTMLButtonElement, TaskChipProps>(
  function TaskChip(
    { filled = false, className, children, type = "button", ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(TASK_CHIP_BASE, chipColor(filled), className)}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

type TaskChipBadgeProps = Omit<
  React.ComponentPropsWithoutRef<"span">,
  "children"
> & {
  filled?: boolean;
  children?: React.ReactNode;
};

/**
 * Non-interactive variant. Same styling, no hover/focus semantics. Use when
 * the chip is a display-only label (e.g. an agent run status that the user
 * can't change directly).
 */
export function TaskChipBadge({
  filled = true,
  className,
  children,
  ...rest
}: TaskChipBadgeProps) {
  return (
    <span
      className={cn(TASK_CHIP_BASE, chipColor(filled), className)}
      {...rest}
    >
      {children}
    </span>
  );
}
